import { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { observer } from 'mobx-react-lite';
import { API_AUTH_URL } from 'api';
import { personalDataStore } from 'stores/personalDataStore';
import NotificationPermissionDialog from 'components/NotificationPermissionDialog';

const DISMISSED_KEY = 'notificationPermissionDismissedAt';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface SocketContextValue {
  socket: Socket | null;
  paymentMessages: any[];
  unreadCount: number;
}

const SocketContext = createContext<SocketContextValue>({
  socket: null,
  paymentMessages: [],
  unreadCount: 0,
});

const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.warn('Notification API not supported');
    return;
  }
  if (Notification.permission === 'default') {
    try {
      const result = await Notification.requestPermission();
      console.log('Notification permission:', result);
    } catch (e) {
      console.warn('Notification permission request failed:', e);
    }
  } else {
    console.log('Notification permission already:', Notification.permission);
  }
};

const SocketProviderComponent = ({ children }: { children: React.ReactNode }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [paymentMessages, setPaymentMessages] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const userId = personalDataStore.userId;

  useEffect(() => {
    if (!userId) return;
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'default') return;

    const dismissedAt = Number(localStorage.getItem(DISMISSED_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return;

    const timer = window.setTimeout(() => setPermissionDialogOpen(true), 1500);
    return () => window.clearTimeout(timer);
  }, [userId]);

  const handleAllow = async () => {
    setPermissionDialogOpen(false);
    await requestNotificationPermission();
  };

  const handleDismiss = () => {
    setPermissionDialogOpen(false);
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  };

  useEffect(() => {
    if (!userId) return;

    // Бэкенд теперь валидирует JWT в handshake (см. WebsocketGateway).
    // userId извлекается из payload — передавать его не надо.
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    const newSocket = io(API_AUTH_URL.replace(/\/+$/, ''), {
      transports: ['websocket'],
      auth: { token: accessToken },
    });

    newSocket.on('connect', () => {
      console.log('Connected to socket server', newSocket.id);
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from socket server');
    });

    newSocket.on('payment:success', (data) => {
      console.log('Payment success received:', data);
      setPaymentMessages((prev) => [...prev, data]);
    });

    newSocket.on('chat:unreadCountChanged', (data) => {
      console.log('chat:unreadCountChanged', data);
      setUnreadCount(data.count);
    });

    newSocket.on('chat:newMessage', (msg) => {
      if (!msg || msg.senderId === userId) return;
      // if (document.visibilityState === 'visible' && document.hasFocus()) return;
      if (!('Notification' in window) || Notification.permission !== 'granted') return;

      const sender = msg.sender || {};
      const name =
        [sender.firstName, sender.lastName].filter(Boolean).join(' ').trim() ||
        sender.email ||
        'Новое сообщение';
      const body =
        msg.type === 'MEETING_INVITE'
          ? 'Приглашение на встречу'
          : (msg.text || '').slice(0, 140);

      const notification = new Notification(name, {
        body,
        icon: '/favicon.ico',
        tag: `chat:${msg.conversationId}`,
      });
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
      setSocket(null);
    };
  }, [userId]);

  return (
    <SocketContext.Provider value={{ socket, paymentMessages, unreadCount }}>
      {children}
      <NotificationPermissionDialog
        open={permissionDialogOpen}
        onClose={handleDismiss}
        onAllow={handleAllow}
      />
    </SocketContext.Provider>
  );
};

export const SocketProvider = observer(SocketProviderComponent);

export const useSocketContext = () => useContext(SocketContext);
